(() => {
  const SELECTORS = {
    root: '[data-landing-product-choice]',
    option: '[data-purchase-option]',
    radio: '[data-purchase-option-radio]',
    details: '[data-option-details]',
    variant: '[data-variant-selector]',
    variantWrapper: '[data-variant-selector-wrapper]',
    variantImage: '.landing-variant-selector__image',
    giftConfig: '[data-landing-gift-config]',
    singleGift: '[data-landing-single-gift]',
    submit: '[data-purchase-submit]',
    status: '[data-purchase-status]',
    primaryVariant: '[data-landing-primary-variant]',
    primaryVariantTitle: '[data-landing-primary-variant-title]',
    displayPrice: '[data-landing-product-price]',
    displayComparePrice: '[data-landing-product-compare-price]',
    displayAvailability: '[data-landing-product-availability]',
    mediaGallery: 'media-gallery',
    controls: 'select, input, button, textarea',
  };

  let currencyRefreshFrame = null;
  const pricingConfigs = new WeakMap();

  function refreshCurrencyDisplay() {
    if (currencyRefreshFrame !== null) return;

    currencyRefreshFrame = window.requestAnimationFrame(() => {
      currencyRefreshFrame = null;

      if (typeof window.baCurr?.refreshConversion === 'function') {
        window.baCurr.refreshConversion();
        return;
      }

      if (typeof window.baCurr?.rePeat === 'function') {
        window.baCurr.rePeat();
      }
    });
  }

  function setMoneyText(element, value) {
    if (!element) return;

    // Nova caches the source amount after conversion. Clear that cache so a
    // variant or offer change is converted from its new Shopify-formatted value.
    element.classList.remove('bacurr-money');
    element.removeAttribute('data-bacurr-orig-money-val');

    if (typeof window.bacurr$ === 'function') {
      window.bacurr$(element).removeData('bacurr-origMoneyVal');
    }

    element.textContent = value;
  }

  function setOptionState(option, active) {
    const radio = option.querySelector(SELECTORS.radio);
    const details = option.querySelector(SELECTORS.details);

    option.classList.toggle('is-active', active);

    if (radio) {
      radio.checked = active;

      if (details) {
        radio.setAttribute('aria-expanded', active ? 'true' : 'false');
      }
    }

    if (!details) return;

    details.hidden = !active;

    details.querySelectorAll(SELECTORS.controls).forEach((control) => {
      control.disabled = !active;
    });
  }

  function activateOption(root, activeRadio) {
    root.querySelectorAll(SELECTORS.option).forEach((option) => {
      const radio = option.querySelector(SELECTORS.radio);

      setOptionState(option, radio === activeRadio);
    });

    updateDisplayedOfferPricing(root);
    updateSingleGiftVisibility(root);
  }

  function isSingleWatch(option) {
    return Number(option?.dataset.paidQuantity) === 1 && Number(option?.dataset.totalQuantity) === 1;
  }

  function updateSingleGiftVisibility(root) {
    const gift = root.querySelector(SELECTORS.singleGift);

    if (gift) gift.hidden = !isSingleWatch(getActiveOption(root));
  }

  function getActiveOption(root) {
    return root.querySelector(`${SELECTORS.option}.is-active`);
  }

  function getSelectedComponents(root, activeOption) {
    if (!activeOption) return [];

    if (activeOption.dataset.usesPrimaryVariant === 'true') {
      const selectedVariantId = Number(root.dataset.selectedVariantId);

      return selectedVariantId ? [{ position: 1, variantId: selectedVariantId }] : [];
    }

    return Array.from(activeOption.querySelectorAll(`${SELECTORS.variant}:not(:disabled)`))
      .map((selector, index) => {
        const wrapper = selector.closest(SELECTORS.variantWrapper);

        return {
          position: Number(wrapper?.dataset.selectorIndex) || index + 1,

          variantId: Number(selector.value),
        };
      })
      .filter((component) => component.variantId);
  }

  function resolveDisplayedPricing(root, option) {
    const activePrimaryVariant = root.querySelector(`${SELECTORS.primaryVariant}[aria-pressed="true"]`);
    if (pricingConfigs.get(root)?.useProductPricing === true) {
      // Offers with item selectors display the first watch's selected variant.
      // Quantities and free items remain exclusively controlled by the offer.
      const selectedOption = option?.querySelector(SELECTORS.variant)?.selectedOptions?.[0];
      const variant = option && option.dataset.usesPrimaryVariant !== 'true'
        ? selectedOption?.dataset || root.dataset
        : activePrimaryVariant?.dataset || root.dataset;

      return {
        price: variant.variantPrice || '',
        comparePrice: variant.variantComparePrice || '',
        saving: variant.variantSaving || '',
      };
    }

    return {
      price: option?.dataset.offerPrice || activePrimaryVariant?.dataset.variantPrice || root.dataset.selectedVariantPrice || '',
      // Historically variant compare-at attributes were empty in offer mode.
      comparePrice: option?.dataset.offerComparePrice || '',
      saving: '',
    };
  }

  function renderDisplayedPricing(price, comparePrice, pricing, saving) {
    setMoneyText(price, pricing.price);

    if (comparePrice) {
      const comparePriceContainer = comparePrice.closest('s');

      setMoneyText(comparePrice, pricing.comparePrice);
      if (comparePriceContainer) {
        comparePriceContainer.hidden = !pricing.comparePrice;
      } else {
        comparePrice.hidden = !pricing.comparePrice;
      }
    }

    if (saving) {
      setMoneyText(saving, pricing.saving);
      saving.parentElement.hidden = !pricing.saving;
    }
  }

  function updateDisplayedOfferPricing(root) {
    renderDisplayedPricing(
      root.querySelector(SELECTORS.displayPrice),
      root.querySelector(SELECTORS.displayComparePrice),
      resolveDisplayedPricing(root, getActiveOption(root))
    );

    if (pricingConfigs.get(root)?.useProductPricing === true) {
      root.querySelectorAll(SELECTORS.option).forEach((option) => {
        renderDisplayedPricing(
          option.querySelector('[data-landing-option-price]'),
          option.querySelector('[data-landing-option-compare-price]'),
          resolveDisplayedPricing(root, option),
          option.querySelector('[data-landing-option-saving]')
        );
      });
    }

    refreshCurrencyDisplay();
  }

  function updateVariantPresentation(selector) {
    const wrapper = selector.closest(SELECTORS.variantWrapper);
    const image = wrapper?.querySelector(SELECTORS.variantImage);
    const selectedOption = selector.selectedOptions?.[0];

    if (!image || !selectedOption) return;

    const imageUrl = selectedOption.dataset.variantImage || '';

    if (!imageUrl) {
      image.hidden = true;
      return;
    }

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = imageUrl;
    image.alt = selectedOption.dataset.variantAlt || selectedOption.textContent.trim();
    image.hidden = false;
  }

  function updatePrimaryVariantPresentation(root, button, updateMedia = true) {
    if (!button) return;

    root.dataset.selectedVariantId = button.dataset.variantId || '';
    root.dataset.selectedVariantAvailable = button.dataset.variantAvailable || 'false';
    root.dataset.selectedVariantPrice = button.dataset.variantPrice || '';
    root.dataset.selectedVariantComparePrice = button.dataset.variantComparePrice || '';

    root.querySelectorAll(SELECTORS.primaryVariant).forEach((variantButton) => {
      const active = variantButton === button;

      variantButton.classList.toggle('is-active', active);
      variantButton.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const title = root.querySelector(SELECTORS.primaryVariantTitle);
    const availability = root.querySelector(SELECTORS.displayAvailability);

    if (title) title.textContent = button.dataset.variantTitle || '';
    updateDisplayedOfferPricing(root);

    if (availability) {
      const availabilityDot = availability.querySelector('.landing-product-purchase__availability-dot');

      availability.textContent = '';
      if (availabilityDot) availability.append(availabilityDot);
      availability.append(document.createTextNode(button.dataset.variantAvailability || ''));
      availability.classList.toggle('is-unavailable', button.dataset.variantAvailable !== 'true');
    }

    if (!updateMedia || !button.dataset.mediaId) return;

    const gallery = root.querySelector(SELECTORS.mediaGallery);
    const mediaId = `${root.dataset.sectionId}-${button.dataset.mediaId}`;

    if (!gallery) return;

    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false);
      return;
    }

    window.customElements?.whenDefined('media-gallery').then(() => {
      gallery.setActiveMedia?.(mediaId, false);
    });
  }

  // Gift with several colors: the chosen swatch becomes the gift variant sent to the cart.
  function updateSingleGiftVariant(variantInput) {
    const card = variantInput.closest('[data-addon-card]');
    const giftInput = card?.querySelector('[data-addon-input]');

    if (!variantInput.checked || !giftInput) return;

    giftInput.value = variantInput.dataset.variantId || '';
    giftInput.dataset.variantId = variantInput.dataset.variantId || '';
    giftInput.dataset.available = variantInput.dataset.available || 'false';

    const selectedTitle = card.querySelector('[data-addon-selected-variant]');
    const image = card.querySelector('.product-addon-card__image');

    if (selectedTitle) selectedTitle.textContent = variantInput.dataset.variantTitle || '';

    if (image && variantInput.dataset.imageSrc) {
      image.src = variantInput.dataset.imageSrc;
      if (variantInput.dataset.imageSrcset) image.srcset = variantInput.dataset.imageSrcset;
      image.alt = variantInput.dataset.imageAlt || '';
    }

    card.querySelectorAll('[data-addon-details-link]').forEach((link) => {
      if (variantInput.dataset.detailsUrl) link.href = variantInput.dataset.detailsUrl;
    });
  }

  // The single gift follows the watch color (gift-color-match.js): the primary variant or, on layouts
  // without one, the single-watch offer's selector.
  function syncSingleGiftColor(root) {
    const gift = root.querySelector(SELECTORS.singleGift);
    if (!gift) return;

    const primaryVariant = root.querySelector(`${SELECTORS.primaryVariant}[aria-pressed="true"]`);
    const singleWatchOption = Array.from(root.querySelectorAll(SELECTORS.option)).find(isSingleWatch);
    const variantColor = primaryVariant
      ? primaryVariant.dataset.variantColor
      : singleWatchOption?.querySelector(SELECTORS.variant)?.selectedOptions?.[0]?.dataset.variantColor;

    window.HeroForceGiftColor?.selectMatchingGifts(gift, variantColor);
  }

  function buildPurchasePayload(root) {
    const purchaseMode = 'standard';
    const activeOption = getActiveOption(root);

    if (!activeOption) return null;

    const giftConfigElement = root.querySelector(SELECTORS.giftConfig);

    let giftConfig = {
      gifts: [],
    };

    if (giftConfigElement) {
      try {
        giftConfig = JSON.parse(giftConfigElement.textContent || '{}');
      } catch (error) {
        console.error('[Landing Gifts]', error);
      }
    }

    const usesPrimaryVariant = activeOption.dataset.usesPrimaryVariant === 'true';

    return {
      productId: root.dataset.productId || null,

      pageHandle: root.dataset.pageHandle || null,

      purchaseMode,

      isSingleWatch: isSingleWatch(activeOption),
      singleGiftSelected: root.querySelector(`${SELECTORS.singleGift} [data-addon-input]`)?.checked === true,
      singleGiftVariantId: root.querySelector(`${SELECTORS.singleGift} [data-addon-input]`)?.dataset.variantId || null,

      includesGifts: activeOption.dataset.includesGifts === 'true',

      selectedVariantAvailable: usesPrimaryVariant ? root.dataset.selectedVariantAvailable === 'true' : true,

      optionIndex: Number(activeOption.dataset.optionIndex),

      offerKey: activeOption.dataset.offerKey || null,

      discountCode: activeOption.dataset.discountCode || null,

      bundleVariantId: activeOption.dataset.bundleVariantId ? Number(activeOption.dataset.bundleVariantId) : null,

      giftConfig,

      paidQuantity: Number(activeOption.dataset.paidQuantity || 0),

      freeQuantity: Number(activeOption.dataset.freeQuantity || 0),

      totalQuantity: Number(activeOption.dataset.totalQuantity || 0),

      components: getSelectedComponents(root, activeOption),
    };
  }

  function initLandingProductChoice(root) {
    if (!root || root.dataset.initialized === 'true') {
      return;
    }

    root.dataset.initialized = 'true';
    pricingConfigs.set(root, { useProductPricing: root.dataset.useProductPricing === 'true' });

    const giftInput = root.querySelector(`${SELECTORS.singleGift} [data-addon-input]`);

    giftInput?.addEventListener('change', () => {
      giftInput.closest('[data-addon-card]')?.classList.toggle('product-addon-card--selected', giftInput.checked);
    });

    root.querySelectorAll(`${SELECTORS.singleGift} [data-addon-variant-input]`).forEach((variantInput) => {
      variantInput.addEventListener('change', () => updateSingleGiftVariant(variantInput));
    });

    const radios = root.querySelectorAll(SELECTORS.radio);

    root.querySelectorAll(SELECTORS.variant).forEach((selector) => {
      updateVariantPresentation(selector);
      selector.addEventListener('change', () => {
        updateVariantPresentation(selector);
        updateDisplayedOfferPricing(root);
        if (isSingleWatch(selector.closest(SELECTORS.option))) syncSingleGiftColor(root);
      });
    });

    const primaryVariants = root.querySelectorAll(SELECTORS.primaryVariant);

    primaryVariants.forEach((button) => {
      button.addEventListener('click', () => {
        const variantChanged = root.dataset.selectedVariantId !== button.dataset.variantId;

        updatePrimaryVariantPresentation(root, button);
        // Re-clicking the current watch keeps a gift color the shopper picked by hand.
        if (variantChanged) syncSingleGiftColor(root);
      });
    });

    const activePrimaryVariant = root.querySelector(`${SELECTORS.primaryVariant}[aria-pressed="true"]`);

    if (activePrimaryVariant) {
      updatePrimaryVariantPresentation(root, activePrimaryVariant, false);
    }

    // The configured gift (landing.single_gift) may not match the watch the page opens on.
    syncSingleGiftColor(root);

    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;

        activateOption(root, radio);
      });
    });

    const checkedRadio = root.querySelector(`${SELECTORS.radio}:checked`);

    if (checkedRadio) {
      activateOption(root, checkedRadio);
    } else {
      updateDisplayedOfferPricing(root);
      updateSingleGiftVisibility(root);
    }

    const submitButton = root.querySelector(SELECTORS.submit);

    const status = root.querySelector(SELECTORS.status);

    if (submitButton) {
      submitButton.addEventListener('click', async () => {
        if (submitButton.disabled) return;

        const payload = buildPurchasePayload(root);

        if (!payload) {
          return;
        }

        if (!payload.selectedVariantAvailable) {
          if (status) {
            status.textContent = 'The selected watch is sold out. Please choose another color.';
          }

          return;
        }

        if (payload.components.length !== payload.totalQuantity) {
          if (status) {
            status.textContent = 'Please select all watch options.';
          }

          return;
        }

        try {
          submitButton.disabled = true;

          if (status) {
            status.textContent = '';
          }

          await addOfferToCart(payload);
        } catch (error) {
          console.error('[Landing Offer]', error);

          if (status) {
            status.textContent = error.message || 'Something went wrong. Please try again.';
          }
        } finally {
          submitButton.disabled = false;
        }
      });
    }
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTORS.root)) {
      initLandingProductChoice(scope);
    }

    scope.querySelectorAll?.(SELECTORS.root).forEach(initLandingProductChoice);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initAll();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  function normalizeVariantId(value) {
    const variantId = String(value || '').trim();
    return /^\d+$/.test(variantId) ? variantId : null;
  }

  function createAddonGroupId() {
    const cryptoApi = window.crypto;

    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

    if (cryptoApi?.getRandomValues) {
      const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    return `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function getOfferGifts(payload) {
    if (payload.isSingleWatch) {
      if (!payload.singleGiftSelected) return [];

      const gift = payload.giftConfig.singleGift;
      // With several colors, only a free variant listed by the page is accepted.
      const chosen = Array.isArray(gift?.variants)
        ? gift.variants.find((variant) => String(variant.variantId) === String(payload.singleGiftVariantId))
        : gift;
      const variantId = normalizeVariantId(chosen?.variantId);

      if (!variantId || chosen?.available !== true || chosen?.price !== 0) {
        throw new Error('The included gift is currently unavailable. Please contact us before purchasing this offer.');
      }

      return [{ variantId, label: String(gift.label || ''), available: true }];
    }

    if (!payload.includesGifts) return [];

    const configuredGifts = Array.isArray(payload.giftConfig.gifts) ? payload.giftConfig.gifts.slice(0, 3) : [];

    const gifts = configuredGifts
      .map((gift) => ({
        variantId: normalizeVariantId(gift?.variantId),
        label: String(gift?.label || ''),
        available: gift?.available !== false,
      }))
      .filter((gift) => gift.variantId && gift.available);

    if (gifts.length !== 3) {
      throw new Error('The three included gifts must be available. Please refresh and try again.');
    }

    return gifts;
  }

  function attachGiftsToOffer(items, gifts, payload) {
    if (gifts.length === 0 || items.length === 0) return items;

    const parentItem = items[0];
    const parentVariantId = normalizeVariantId(parentItem.id);

    if (!parentVariantId) {
      throw new Error('The selected watch variant is invalid. Please refresh and try again.');
    }

    const groupId = createAddonGroupId();
    const addonSetName = String(payload.offerKey || 'watch_gifts');

    parentItem.properties = {
      ...parentItem.properties,
      _addon_group_id: groupId,
      _addon_role: 'parent',
      _addon_configuration_id: groupId,
      _addon_set_name: addonSetName,
      _addon_expected_child_count: String(gifts.length),
    };

    gifts.forEach((gift, index) => {
      const giftProperties = {
        _landing_gift: 'true',
        _landing_gift_position: String(index + 1),
        _landing_offer: payload.offerKey,
        _addon_group_id: groupId,
        _addon_role: 'child',
        _addon_kind: 'free_gift',
        _addon_quantity_policy: 'fixed_one',
        _addon_parent_variant_id: parentVariantId,
        _addon_label: gift.label,
        _addon_configuration_id: groupId,
        _addon_set_name: addonSetName,
      };

      if (payload.optionIndex !== null) {
        giftProperties._landing_option = String(payload.optionIndex);
      }

      items.push({
        id: gift.variantId,
        quantity: 1,
        parent_id: parentVariantId,
        properties: giftProperties,
      });
    });

    return items;
  }

  async function addOfferToCart(payload) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (!cart || typeof cart.getSectionsToRender !== 'function' || typeof cart.renderContents !== 'function') {
      throw new Error('The cart drawer is unavailable. Please refresh and try again.');
    }

    const sections = cart.getSectionsToRender().map((section) => section.id);
    const items = payload.components.map((component) => {
      const position = Number(component.position);
      const variantId = normalizeVariantId(component.variantId);
      const isFree = position > Number(payload.paidQuantity);

      if (!variantId) {
        throw new Error('A selected watch variant is invalid. Please select it again.');
      }

      const properties = {
        _landing_offer: payload.offerKey,
      };

      if (payload.purchaseMode === 'standard') {
        properties._landing_option = String(payload.optionIndex);
        properties._landing_position = String(position);
        properties._landing_role = isFree ? 'free' : 'paid';
      }

      return {
        id: variantId,
        quantity: 1,
        properties,
      };
    });

    attachGiftsToOffer(items, getOfferGifts(payload), payload);
    cart.setActiveElement(document.activeElement);

    const addConfig = fetchConfig('javascript');
    addConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
    addConfig.body = JSON.stringify({
      items,
      sections,
      sections_url: window.location.pathname,
    });

    const addResponse = await fetch(`${routes.cart_add_url}`, addConfig);
    const addedCart = await addResponse.json().catch(() => null);

    if (!addResponse.ok || addedCart?.status) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'landing-product-choice',
        productVariantId: items[0]?.id,
        errors: addedCart?.errors || addedCart?.description,
        message: addedCart?.message,
      });

      throw new Error(addedCart?.description || addedCart?.message || 'Unable to add the selected watches and gifts.');
    }

    /*
     * Important :
     * - promo → applique son code
     * - Single → retire un éventuel ancien code landing
     */
    const discountConfig = fetchConfig('javascript');
    discountConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
    discountConfig.body = JSON.stringify({
      discount: payload.discountCode || '',
      sections,
      sections_url: window.location.pathname,
    });

    const discountResponse = await fetch(`${routes.cart_update_url}`, discountConfig);
    const updatedCart = await discountResponse.json().catch(() => null);

    if (!discountResponse.ok || updatedCart?.status) {
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'landing-product-choice',
        productVariantId: items[0]?.id,
        errors: updatedCart?.errors || updatedCart?.description,
        message: updatedCart?.message,
      });

      throw new Error(updatedCart?.description || updatedCart?.message || 'Unable to apply the offer.');
    }

    const cartState = updatedCart?.sections ? updatedCart : addedCart;

    if (!cartState?.sections) {
      throw new Error('The items were added, but the cart drawer could not refresh.');
    }

    const addedItem = Array.isArray(addedCart?.items)
      ? addedCart.items.find((item) => item?.properties?._addon_role === 'parent') || addedCart.items[0]
      : null;
    const cartRenderResponse = {
      ...cartState,
      ...addedItem,
      items: cartState.items,
      sections: cartState.sections,
      id: addedItem?.id || items[0]?.id,
    };
    const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'landing-product-choice',
      productVariantId: items[0]?.id,
      cartData: cartRenderResponse,
    }).then(() => {
      CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
    });

    CartPerformance.measure('add:paint-updated-sections', () => {
      cart.renderContents(cartRenderResponse);
    });

    if (cart.classList.contains('is-empty')) {
      cart.classList.remove('is-empty');
    }
  }
})();
